import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';

/**
 * SaleApiLogsViewer - View API logs and step details for Sale Order workflow executions
 * This is a separate component from the main Debug ApiLogsViewer to avoid conflicts
 */
const SaleApiLogsViewer = ({ executionId, onClose }) => {
  const [apiCalls, setApiCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCall, setSelectedCall] = useState(null);
  const [executionSummary, setExecutionSummary] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    request: false,
    response: false,
    headers: false,
    toolCalls: true
  });

  useEffect(() => {
    if (executionId) {
      fetchExecutionDetails();
    }
  }, [executionId]);

  const fetchExecutionDetails = async () => {
    try {
      setLoading(true);
      const token = Cookies.get('sankalvax_token');

      // Use Sale Order Agent specific endpoint
      const response = await fetch(`/api/sale/workflows/debug/${executionId}/execution-details`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch execution details: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        // Transform data for display
        const transformedApiCalls = [];

        data.execution_details.forEach(step => {
          if (step.step_type === 'ui_step') {
            // UI Step
            transformedApiCalls.push({
              id: step.step_execution_id,
              step_execution_id: step.step_execution_id,
              step_name: step.step_name,
              step_type: step.step_type,
              step_status: step.status,
              step_duration_ms: step.execution_time_ms,
              step_started_at: step.started_at,
              step_completed_at: step.completed_at,
              provider: null,
              model: null,
              request_url: null,
              request_body: step.input_data,
              response_body: step.output_data,
              response_status: step.status === 'completed' ? 200 : step.status === 'failed' ? 500 : null,
              duration_ms: step.execution_time_ms,
              tokens_used: step.tokens_used,
              cost: step.cost,
              request_timestamp: step.started_at,
              response_timestamp: step.completed_at,
              error_message: step.error_message,
              is_ui_step: true
            });
          } else if (step.api_calls && step.api_calls.length > 0) {
            // AI Step with API calls
            step.api_calls.forEach(call => {
              transformedApiCalls.push({
                id: call.id,
                step_execution_id: step.step_execution_id,
                step_name: step.step_name,
                step_type: step.step_type,
                step_status: step.status,
                step_duration_ms: step.execution_time_ms,
                step_started_at: step.started_at,
                step_completed_at: step.completed_at,
                provider: call.provider,
                model: call.model,
                request_url: call.request_url,
                request_body: call.request_body,
                request_headers: call.request_headers,
                response_body: call.response_body,
                response_headers: call.response_headers,
                response_status: call.response_status,
                duration_ms: call.duration_ms,
                tokens_used: call.tokens_used,
                cost: call.cost,
                request_timestamp: call.request_timestamp,
                response_timestamp: call.response_timestamp,
                error_message: step.error_message,
                is_ui_step: false
              });
            });
          } else {
            // AI Step without API calls
            transformedApiCalls.push({
              id: step.step_execution_id,
              step_execution_id: step.step_execution_id,
              step_name: step.step_name,
              step_type: step.step_type,
              step_status: step.status,
              step_duration_ms: step.execution_time_ms,
              step_started_at: step.started_at,
              step_completed_at: step.completed_at,
              provider: 'N/A',
              model: 'No API calls made',
              request_url: null,
              request_body: step.input_data,
              response_body: step.output_data,
              response_status: step.status === 'completed' ? 200 : step.status === 'failed' ? 500 : null,
              duration_ms: step.execution_time_ms,
              tokens_used: step.tokens_used,
              cost: step.cost,
              request_timestamp: step.started_at,
              response_timestamp: step.completed_at,
              error_message: step.error_message,
              is_ui_step: false
            });
          }
        });

        setApiCalls(transformedApiCalls);
        setExecutionSummary({
          total_steps: data.total_steps,
          total_api_calls: data.total_api_calls,
          run_id: data.run_id,
          status: data.status,
          workflow_id: data.workflow_id
        });
        setError(null);

        if (transformedApiCalls.length > 0) {
          setSelectedCall(transformedApiCalls[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching execution details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    return `${ms}ms`;
  };

  const formatJson = (obj) => {
    return JSON.stringify(obj, null, 2);
  };

  const extractToolCalls = (call) => {
    const toolCalls = [];

    // Extract from request body
    if (call.request_body && call.request_body.messages) {
      const messages = call.request_body.messages;
      messages.forEach((message, msgIndex) => {
        if (message.role === 'assistant' && message.tool_calls) {
          message.tool_calls.forEach((toolCall) => {
            let parsedArgs = {};
            try {
              if (toolCall.function?.arguments) {
                parsedArgs = typeof toolCall.function.arguments === 'string'
                  ? JSON.parse(toolCall.function.arguments)
                  : toolCall.function.arguments;
              }
            } catch (e) {
              parsedArgs = { error: 'Failed to parse arguments' };
            }

            toolCalls.push({
              type: 'outgoing',
              id: toolCall.id,
              name: toolCall.function?.name || toolCall.name,
              arguments: JSON.stringify(parsedArgs, null, 2),
              messageIndex: msgIndex,
              source: 'request'
            });
          });
        }

        if (message.role === 'tool') {
          toolCalls.push({
            type: 'result',
            id: message.tool_call_id,
            name: 'Tool Result',
            content: message.content,
            messageIndex: msgIndex,
            source: 'request'
          });
        }
      });
    }

    // Extract from response body
    if (call.response_body && call.response_body.choices) {
      const message = call.response_body.choices[0]?.message;
      if (message && message.tool_calls) {
        message.tool_calls.forEach((toolCall) => {
          let parsedArgs = {};
          try {
            if (toolCall.function?.arguments) {
              parsedArgs = typeof toolCall.function.arguments === 'string'
                ? JSON.parse(toolCall.function.arguments)
                : toolCall.function.arguments;
            }
          } catch (e) {
            parsedArgs = { error: 'Failed to parse arguments' };
          }

          toolCalls.push({
            type: 'requested',
            id: toolCall.id,
            name: toolCall.function?.name || toolCall.name,
            arguments: JSON.stringify(parsedArgs, null, 2),
            source: 'response'
          });
        });
      }
    }

    return toolCalls;
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const getStatusColor = (status) => {
    if (status >= 200 && status < 300) return 'text-green-600';
    if (status >= 400 && status < 500) return 'text-red-600';
    if (status >= 500) return 'text-red-800';
    return 'text-gray-600';
  };

  const getStepStatusBadge = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-800',
      running: 'bg-yellow-100 text-yellow-800',
      failed: 'bg-red-100 text-red-800',
      pending: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.pending;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="flex items-center gap-3">
          <svg className="w-6 h-6 animate-spin text-orange-500" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          <span className="text-gray-600">Loading Sale Order execution details...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-600">
          <span className="font-medium">Run ID:</span>
          <span className="ml-2 font-mono bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs">
            {executionId?.substring(0, 8)}...
          </span>
          <span className="ml-2 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
            Sale Order Agent
          </span>
        </div>
        <button
          onClick={onClose}
          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
        >
          Back to Executions
        </button>
      </div>

      {/* Execution Summary */}
      {executionSummary && (
        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
          <h3 className="text-lg font-semibold mb-3 text-orange-800">Sale Order Execution Summary</h3>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Status:</span>
              <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${getStepStatusBadge(executionSummary.status)}`}>
                {executionSummary.status}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Total Steps:</span>
              <span className="ml-2 font-mono font-semibold text-orange-700">{executionSummary.total_steps || 0}</span>
            </div>
            <div>
              <span className="text-gray-600">API Calls:</span>
              <span className="ml-2 font-mono font-semibold text-orange-700">{executionSummary.total_api_calls || 0}</span>
            </div>
            <div>
              <span className="text-gray-600">Run ID:</span>
              <span className="ml-2 font-mono text-xs bg-orange-100 px-2 py-1 rounded">{executionSummary.run_id?.substring(0, 8)}...</span>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
          <div className="text-red-600 font-medium">Error loading execution details:</div>
          <div className="mt-1 text-sm text-red-500">{error}</div>
        </div>
      )}

      {apiCalls.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-gray-500">No steps found for this execution</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Steps List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow border">
              <div className="px-4 py-3 border-b bg-gray-50">
                <h3 className="font-medium text-gray-900">Execution Steps ({apiCalls.length})</h3>
              </div>
              <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
                {apiCalls.map((call, index) => (
                  <div
                    key={call.id}
                    onClick={() => setSelectedCall(call)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 transition ${
                      selectedCall?.id === call.id ? 'bg-orange-50 border-r-4 border-orange-500' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium text-gray-900">
                        #{index + 1} - {call.step_name}
                      </span>
                      <span className={`text-sm font-mono ${getStatusColor(call.response_status)}`}>
                        {call.response_status || 'N/A'}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 space-y-1">
                      {call.is_ui_step ? (
                        <div className="flex items-center gap-1 text-blue-600">
                          <span>UI Step</span>
                        </div>
                      ) : (
                        <div>{call.provider} - {call.model}</div>
                      )}
                      <div>{formatDuration(call.duration_ms)}</div>
                      {!call.is_ui_step && call.tokens_used > 0 && (
                        <div>{call.tokens_used} tokens</div>
                      )}
                      {!call.is_ui_step && extractToolCalls(call).length > 0 && (
                        <div className="text-purple-600 font-medium">
                          {extractToolCalls(call).length} tool call(s)
                        </div>
                      )}
                    </div>
                    {call.error_message && (
                      <div className="mt-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded truncate">
                        Error: {call.error_message.substring(0, 50)}...
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Selected Step Details */}
          <div className="lg:col-span-2 space-y-4">
            {selectedCall ? (
              <>
                {/* Step Info */}
                <div className="bg-white rounded-lg shadow border">
                  <div className="px-4 py-3 border-b bg-orange-50">
                    <h3 className="font-medium text-orange-800">Step Information</h3>
                  </div>
                  <div className="p-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <label className="font-medium text-gray-700">Step Name:</label>
                        <div className="font-mono text-sm bg-gray-100 px-2 py-1 rounded mt-1">
                          {selectedCall.step_name}
                        </div>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">Step Type:</label>
                        <div className="font-mono">{selectedCall.step_type}</div>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">Status:</label>
                        <div className={`font-mono font-semibold ${
                          selectedCall.step_status === 'completed' ? 'text-green-600' :
                          selectedCall.step_status === 'running' ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {selectedCall.step_status}
                        </div>
                      </div>
                      <div>
                        <label className="font-medium text-gray-700">Duration:</label>
                        <div className="font-mono">{formatDuration(selectedCall.step_duration_ms)}</div>
                      </div>
                      {!selectedCall.is_ui_step && (
                        <>
                          <div>
                            <label className="font-medium text-gray-700">Provider:</label>
                            <div>{selectedCall.provider}</div>
                          </div>
                          <div>
                            <label className="font-medium text-gray-700">Model:</label>
                            <div>{selectedCall.model}</div>
                          </div>
                          <div>
                            <label className="font-medium text-gray-700">Tokens:</label>
                            <div>{selectedCall.tokens_used || 0}</div>
                          </div>
                          <div>
                            <label className="font-medium text-gray-700">Cost:</label>
                            <div>${(selectedCall.cost || 0).toFixed(6)}</div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tool Calls */}
                {!selectedCall.is_ui_step && extractToolCalls(selectedCall).length > 0 && (
                  <div className="bg-white rounded-lg shadow border">
                    <button
                      onClick={() => toggleSection('toolCalls')}
                      className="w-full px-4 py-3 border-b bg-purple-50 flex justify-between items-center"
                    >
                      <h3 className="font-medium text-purple-800">
                        Tool Calls ({extractToolCalls(selectedCall).length})
                      </h3>
                      <span>{expandedSections.toolCalls ? '▼' : '▶'}</span>
                    </button>
                    {expandedSections.toolCalls && (
                      <div className="p-4 space-y-4">
                        {extractToolCalls(selectedCall).map((toolCall, index) => (
                          <div key={`${toolCall.id}-${index}`} className="border border-purple-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                  toolCall.type === 'requested'
                                    ? 'bg-blue-100 text-blue-800'
                                    : toolCall.type === 'outgoing'
                                    ? 'bg-orange-100 text-orange-800'
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                  {toolCall.type === 'requested' ? 'AI Requested' :
                                   toolCall.type === 'outgoing' ? 'Executing' : 'Result'}
                                </span>
                                <span className="text-lg font-bold text-gray-900">
                                  {toolCall.name || 'Unknown Tool'}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500 font-mono">
                                ID: {toolCall.id}
                              </span>
                            </div>

                            {toolCall.arguments && (
                              <div className="mb-4">
                                <label className="text-sm font-semibold text-purple-700 mb-2 block">
                                  Request Parameters:
                                </label>
                                <pre className="text-sm bg-purple-50 p-4 rounded-lg border overflow-x-auto font-mono">
                                  {toolCall.arguments}
                                </pre>
                              </div>
                            )}

                            {toolCall.content && (
                              <div>
                                <label className="text-sm font-semibold text-green-700 mb-2 block">
                                  Tool Response:
                                </label>
                                <pre className="text-sm bg-green-50 p-4 rounded-lg border overflow-x-auto font-mono max-h-48 overflow-y-auto">
                                  {toolCall.content}
                                </pre>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Request Body */}
                <div className="bg-white rounded-lg shadow border">
                  <button
                    onClick={() => toggleSection('request')}
                    className="w-full px-4 py-3 border-b bg-gray-50 flex justify-between items-center"
                  >
                    <h3 className="font-medium">
                      {selectedCall.is_ui_step ? 'Input Data' : 'Request Body'}
                    </h3>
                    <span>{expandedSections.request ? '▼' : '▶'}</span>
                  </button>
                  {expandedSections.request && (
                    <div className="p-4">
                      <pre className="text-xs bg-gray-50 p-4 rounded overflow-x-auto border max-h-96 overflow-y-auto">
                        {formatJson(selectedCall.request_body)}
                      </pre>
                    </div>
                  )}
                </div>

                {/* Response Body */}
                <div className="bg-white rounded-lg shadow border">
                  <button
                    onClick={() => toggleSection('response')}
                    className="w-full px-4 py-3 border-b bg-gray-50 flex justify-between items-center"
                  >
                    <h3 className="font-medium">
                      {selectedCall.is_ui_step ? 'Output Data' : 'Response Body'}
                    </h3>
                    <span>{expandedSections.response ? '▼' : '▶'}</span>
                  </button>
                  {expandedSections.response && (
                    <div className="p-4">
                      {selectedCall.error_message ? (
                        <div className="text-red-600 bg-red-50 p-4 rounded border border-red-200 font-mono text-sm">
                          {selectedCall.error_message}
                        </div>
                      ) : (
                        <pre className="text-xs bg-gray-50 p-4 rounded overflow-x-auto border max-h-96 overflow-y-auto">
                          {formatJson(selectedCall.response_body)}
                        </pre>
                      )}
                    </div>
                  )}
                </div>

                {/* Headers */}
                {!selectedCall.is_ui_step && (
                  <div className="bg-white rounded-lg shadow border">
                    <button
                      onClick={() => toggleSection('headers')}
                      className="w-full px-4 py-3 border-b bg-gray-50 flex justify-between items-center"
                    >
                      <h3 className="font-medium">Headers</h3>
                      <span>{expandedSections.headers ? '▼' : '▶'}</span>
                    </button>
                    {expandedSections.headers && (
                      <div className="p-4 space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-700 mb-2">Request Headers:</h4>
                          <pre className="text-xs bg-gray-50 p-3 rounded overflow-x-auto border">
                            {formatJson(selectedCall.request_headers)}
                          </pre>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-700 mb-2">Response Headers:</h4>
                          <pre className="text-xs bg-gray-50 p-3 rounded overflow-x-auto border">
                            {formatJson(selectedCall.response_headers)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-lg shadow border p-8 text-center text-gray-500">
                Select a step from the list to view details
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SaleApiLogsViewer;
